import { useCallback, useMemo } from 'react';
import { usePlatformSettings } from '../context/PlatformSettingsContext';
import { useWorkspace } from '../context/WorkspaceContext';
import {
  formatDateTimeInZone,
  resolveDisplayTimeZone,
  timeZoneDisplayName,
} from '../lib/timezone';

/**
 * Effective display timezone for the current user (company override → platform default).
 * @param {{ role?: string } | null | undefined} [userData]
 */
export function useDisplayTimeZone(userData) {
  const { settings: platformSettings } = usePlatformSettings();
  const { workspace } = useWorkspace();

  const timeZone = useMemo(
    () => resolveDisplayTimeZone(platformSettings, workspace),
    [platformSettings, workspace],
  );

  const timeZoneLabel = useMemo(() => timeZoneDisplayName(timeZone), [timeZone]);

  const formatDateTime = useCallback(
    (value, options) => formatDateTimeInZone(value, timeZone, options),
    [timeZone],
  );

  const canManage = useMemo(() => {
    const role = userData?.role;
    if (role === 'super_admin') return { platform: true, company: true };
    const delegated = platformSettings?.timezoneDelegateRoles?.includes(role);
    return { platform: false, company: Boolean(delegated) };
  }, [userData?.role, platformSettings?.timezoneDelegateRoles]);

  return {
    timeZone,
    timeZoneLabel,
    formatDateTime,
    platformSettings,
    canManageTimezone: canManage,
  };
}
